import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 麻豆在线`,
        description: currentCategory.description,
        url: `https://www.madouzx.com/category/${activeCategory}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.madouzx.com/' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name} - 麻豆在线`}
        description={currentCategory.description}
        keywords={`麻豆在线,${currentCategory.name},麻豆在线高清,麻豆在线资源导航`}
        canonicalUrl={`https://www.madouzx.com/category/${activeCategory}`}
        schema={pageSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
              {currentCategory.name}
            </h1>
            <p className="text-foreground-600 mt-2">{currentCategory.description}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="w-full lg:w-56 flex-shrink-0">
              <nav className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0" aria-label="分类导航">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </nav>
            </aside>

            <div className="flex-1 min-w-0">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {article.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{article.author}</span>
                          <div className="flex items-center gap-3">
                            <span>{article.date}</span>
                            <span>{article.readTime} 阅读</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}